import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, PageLayout } from '@sentinel/ui';
import { useUser } from '@sentinel/auth-core';
import { ConfirmDialog } from '../features/security/components/confirm-dialog';
import { ExportCard } from '../features/data/components/export-card';
import { DeleteCard, emailMatches } from '../features/data/components/delete-card';
import { IdpManagedDeleteCard } from '../features/data/components/idp-managed-delete-card';
import { useExportData } from '../features/data/use-export';
import { useDeleteAccount } from '../features/data/use-delete';

function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}

function DataPage() {
  const { t } = useTranslation('data');
  const navigate = useNavigate();
  const user = useUser();

  const exportMutation = useExportData();
  const deleteMutation = useDeleteAccount();

  const [emailInput, setEmailInput] = useState('');
  const [password, setPassword] = useState('');

  const [exportError, setExportError] = useState<string | null>(null);
  const [exportFilename, setExportFilename] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);

  async function handleExport() {
    setExportError(null);
    setExportFilename(null);
    try {
      const result = await exportMutation.mutateAsync();
      setExportFilename(result.filename);
    } catch (e) {
      const status = getStatus(e);
      if (status === 403) {
        // Step-up middleware intercepted the cancel/failure; stay quiet.
        return;
      }
      setExportError(t('export_failed'));
    }
  }

  function handleDeleteClick() {
    if (!user) return;
    if (!emailMatches(emailInput, user.email)) return;
    if (password.length === 0) return;
    setDeleteError(null);
    setConfirmOpen(true);
  }

  async function handleDeleteConfirm() {
    setConfirmOpen(false);
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync({ password });
      // Backend cleared session cookies. Drop into /login.
      await navigate({ to: '/login' });
    } catch (e) {
      const status = getStatus(e);
      if (status === 403) {
        // Step-up middleware took the lead.
        return;
      }
      if (status === 401) {
        setDeleteError(t('delete_password_invalid'));
        return;
      }
      setDeleteError(t('delete_failed'));
    }
  }

  const userEmail = user?.email ?? '';

  return (
    <>
      <PageLayout title={t('title')} subtitle={t('subtitle')}>
        {!user ? (
          <Alert type="info" role="status">
            {t('generic_error')}
          </Alert>
        ) : null}

        <ExportCard
          pending={exportMutation.isPending}
          successFilename={exportFilename}
          errorMessage={exportError}
          onExport={handleExport}
        />

        {user?.localAuthManagedByIdp ? (
          <IdpManagedDeleteCard />
        ) : (
          <DeleteCard
            email={userEmail}
            emailInput={emailInput}
            password={password}
            pending={deleteMutation.isPending}
            errorMessage={deleteError}
            onEmailChange={setEmailInput}
            onPasswordChange={setPassword}
            onDeleteClick={handleDeleteClick}
          />
        )}
      </PageLayout>

      <ConfirmDialog
        open={confirmOpen}
        title={t('delete_confirm_title')}
        subtitle={t('delete_confirm_message')}
        submitLabel={t('delete_confirm_submit')}
        cancelLabel={t('delete_confirm_cancel')}
        submitting={deleteMutation.isPending}
        variant="danger"
        onSubmit={handleDeleteConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

export const Route = createFileRoute('/account/data')({
  component: DataPage,
});
